import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Paper,
  Chip,
  Alert,
  Button,
  Stack,
  IconButton,
  Collapse,
  Radio,
  Divider,
  alpha,
  useTheme,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  Info as InfoIcon,
  Gavel as GavelIcon,
  Work as WorkIcon,
  Security as SecurityIcon,
  HealthAndSafety as HealthIcon,
  DirectionsCar as CarIcon,
  AccountBalance as AccountBalanceIcon,
  Folder as FolderIcon,
  CloudUpload as CloudUploadIcon,
  Article as ArticleIcon
} from '@mui/icons-material';
import { DocumentType, AREAS } from '../documentTypes';
import AudioRecorder from '../../AudioRecorder';

// ========================================
// TIPOS E INTERFACES
// ========================================

export interface FormProps {
  documentType: DocumentType;
  values: Record<string, any>;
  onChange: (values: Record<string, any>) => void;
  uploadedFiles: File[];
  onFileUpload: (files: File[]) => void;
  onFileRemove: (index: number) => void;
}

// ========================================
// CONSTANTES COMPARTILHADAS
// ========================================

// Ícones por área
export const AREA_ICONS: Record<string, React.ReactNode> = {
  civel: <GavelIcon sx={{ fontSize: 24 }} />,
  trabalhista: <WorkIcon sx={{ fontSize: 24 }} />,
  criminal: <SecurityIcon sx={{ fontSize: 24 }} />,
  previdenciario: <HealthIcon sx={{ fontSize: 24 }} />,
  transito: <CarIcon sx={{ fontSize: 24 }} />,
  magistratura: <AccountBalanceIcon sx={{ fontSize: 24 }} />,
  recesso: <FolderIcon sx={{ fontSize: 24 }} />,
  ambiental: <FolderIcon sx={{ fontSize: 24 }} />,
  familia: <FolderIcon sx={{ fontSize: 24 }} />,
  geral: <DescriptionIcon sx={{ fontSize: 24 }} />,
};

// Estilos de escrita disponíveis
export const WRITING_STYLES = [
  {
    id: 'padrao',
    name: 'Padrão Jurídico AI',
    description: 'Estilo profissional equilibrado entre formalidade e clareza',
  },
  {
    id: 'formal',
    name: 'Formal Tradicional',
    description: 'Linguagem altamente formal e técnica',
  },
  {
    id: 'objetivo',
    name: 'Objetivo e Conciso',
    description: 'Direto ao ponto, sem floreios',
  },
  {
    id: 'detalhado',
    name: 'Detalhado e Fundamentado',
    description: 'Argumentação extensa com muitas referências',
  },
];

// ========================================
// COMPONENTES COMPARTILHADOS
// ========================================

// Cabeçalho do formulário com tipo selecionado
export const FormHeader: React.FC<{
  documentType: DocumentType;
  customTitle?: string;
  customDescription?: string;
}> = ({ documentType, customTitle, customDescription }) => {
  const theme = useTheme();
  const areaConfig = AREAS.find(a => a.id === documentType.area);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 3,
        bgcolor: alpha(areaConfig?.color || theme.palette.primary.main, 0.06),
        border: `1px solid ${alpha(areaConfig?.color || theme.palette.primary.main, 0.2)}`,
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(areaConfig?.color || theme.palette.primary.main, 0.15),
            color: areaConfig?.color || theme.palette.primary.main,
          }}
        >
          {AREA_ICONS[documentType.area] || <DescriptionIcon sx={{ fontSize: 24 }} />}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={600} color="text.primary">
            {customTitle || documentType.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {customDescription || documentType.description}
          </Typography>
        </Box>
        <Chip
          label={areaConfig?.name || documentType.area}
          sx={{
            bgcolor: alpha(areaConfig?.color || '#4a5568', 0.15),
            color: areaConfig?.color || 'text.secondary',
            fontWeight: 500,
          }}
        />
      </Box>
    </Paper>
  );
};

// Campo de nome do cliente
export const ClientNameField: React.FC<{
  value: string;
  onChange: (value: string) => void;
  label?: string;
}> = ({ value, onChange, label = 'Escreva o nome do seu cliente:' }) => (
  <Box>
    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
      {label} <Typography component="span" color="error.main">*</Typography>
    </Typography>
    <TextField
      fullWidth
      placeholder="Ex: João da Silva"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      helperText="Separe os nomes com ';' caso você represente mais de uma parte"
    />
  </Box>
);

// Área de upload de documentos com drag-and-drop
export const DocumentUploadArea: React.FC<{
  uploadedFiles: File[];
  onFileUpload: (files: File[]) => void;
  onFileRemove: (index: number) => void;
  suggestedDocs: string | React.ReactNode;
  fileInputRef: React.RefObject<HTMLInputElement>;
}> = ({ uploadedFiles, onFileUpload, onFileRemove, suggestedDocs, fileInputRef }) => {
  const theme = useTheme();
  const [isDragging, setIsDragging] = React.useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      onFileUpload(files);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      onFileUpload(Array.from(files));
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          borderStyle: 'dashed',
          borderWidth: 2,
          borderColor: isDragging ? 'primary.main' : 'grey.300',
          bgcolor: isDragging ? alpha(theme.palette.primary.main, 0.05) : 'grey.50',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Faça upload do processo ou das peças relevantes
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Arraste e solte os arquivos aqui ou <strong>clique para enviar</strong>
          </Typography>

          <Alert
            severity="info"
            icon={<ArticleIcon />}
            sx={{
              textAlign: 'left',
              mb: 2,
              '& .MuiAlert-message': { width: '100%' }
            }}
          >
            {typeof suggestedDocs === 'string' ? (
              <>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                  Sugestão de documentos para anexar:
                </Typography>
                <Typography variant="body2">
                  {suggestedDocs}
                </Typography>
              </>
            ) : (
              suggestedDocs
            )}
          </Alert>

          <Typography variant="caption" color="text.secondary">
            Você pode enviar um ou mais arquivos, desde que o total não ultrapasse 150 MB ou 1.000 páginas.
          </Typography>
        </Box>

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.docx,.txt,audio/*,image/*"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
      </Paper>

      {/* Lista de arquivos enviados */}
      {uploadedFiles.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Arquivos enviados ({uploadedFiles.length})
          </Typography>
          <Stack spacing={1}>
            {uploadedFiles.map((file, index) => (
              <Paper
                key={index}
                variant="outlined"
                sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}
              >
                <DescriptionIcon sx={{ color: 'primary.main' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500} noWrap>
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(file.size / 1024).toFixed(1)} KB
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => { e.stopPropagation(); onFileRemove(index); }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}
    </>
  );
};

// Campo de descrição dos fatos (opcional)
export const FactsDescriptionField: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  minChars?: number;
}> = ({
  value,
  onChange,
  placeholder = "Descreva os fatos relevantes do caso...",
  required = false,
  minChars = 0
}) => (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        Conte mais sobre o caso trazendo a realidade dos fatos, se achar necessário:
        {required ? (
          <Typography component="span" color="error.main"> *</Typography>
        ) : (
          <Typography component="span" color="text.secondary" fontWeight={400}> (Opcional)</Typography>
        )}
      </Typography>
      <TextField
        fullWidth
        multiline
        rows={5}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        inputProps={{ maxLength: 10000 }}
      />
      <Typography
        variant="caption"
        color={minChars > 0 && (value || '').length >= minChars ? 'success.main' : 'text.secondary'}
        sx={{ mt: 0.5, display: 'block' }}
      >
        {(value || '').length} de 10000 caracteres
        {minChars > 0 && (value || '').length < minChars && ` (mínimo: ${minChars})`}
        {minChars > 0 && (value || '').length >= minChars && ' ✓'}
      </Typography>
    </Box>
  );

// Seletor de peça a rebater
export const PieceToRebutField: React.FC<{
  value: string;
  onChange: (value: string) => void;
  otherValue?: string;
  onOtherChange?: (value: string) => void;
  options: string[];
  label?: string;
}> = ({
  value,
  onChange,
  otherValue = '',
  onOtherChange,
  options,
  label = 'Indique a peça do processo que deseja rebater ou responder:'
}) => (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        {label} <Typography component="span" color="error.main">*</Typography>
      </Typography>
      <FormControl fullWidth>
        <Select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          displayEmpty
        >
          <MenuItem value="" disabled>
            <em>Selecione a peça</em>
          </MenuItem>
          {options.map((peca) => (
            <MenuItem key={peca} value={peca}>{peca}</MenuItem>
          ))}
        </Select>
      </FormControl>
      {value === 'Outro' && onOtherChange && (
        <TextField
          fullWidth
          placeholder="Especifique qual peça deseja rebater..."
          value={otherValue}
          onChange={(e) => onOtherChange(e.target.value)}
          sx={{ mt: 1 }}
        />
      )}
    </Box>
  );

// Seção de gravação de áudio
export const AudioRecordingSection: React.FC<{
  onRecordingComplete: (audioBlob: Blob) => void;
  description?: string;
}> = ({ onRecordingComplete, description = "Grave um áudio descrevendo os fatos do caso que a IA irá transcrever automaticamente." }) => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
      <Typography variant="subtitle2" fontWeight={600}>
        Prefere gravar um áudio?
      </Typography>
      <Chip label="Opcional" size="small" variant="outlined" />
    </Box>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      {description}
    </Typography>
    <AudioRecorder
      onRecordingComplete={onRecordingComplete}
      disabled={false}
    />
  </Paper>
);

// Seletor de estilo de escrita
export const WritingStyleSelector: React.FC<{
  selectedStyle: string;
  onStyleChange: (styleId: string) => void;
}> = ({ selectedStyle, onStyleChange }) => {
  const theme = useTheme();

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <DescriptionIcon sx={{ color: 'text.secondary' }} />
        <Typography variant="subtitle1" fontWeight={600}>
          Estilo de escrita
        </Typography>
        <Tooltip title="O estilo de escrita define o tom e a complexidade do documento gerado. Escolha a opção que melhor se adapta ao perfil do seu cliente e à estratégia processual." arrow>
          <InfoIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
        </Tooltip>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {WRITING_STYLES.map((style) => (
          <Paper
            key={style.id}
            variant="outlined"
            onClick={() => onStyleChange(style.id)}
            sx={{
              p: 2,
              cursor: 'pointer',
              border: selectedStyle === style.id ? `2px solid ${theme.palette.primary.main}` : '1px solid',
              borderColor: selectedStyle === style.id ? 'primary.main' : 'grey.200',
              bgcolor: selectedStyle === style.id ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.02),
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Radio
                checked={selectedStyle === style.id}
                size="small"
                sx={{ p: 0, mr: 0.5 }}
              />
              <Typography variant="subtitle2" fontWeight={600}>
                {style.name}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 3.5 }}>
              {style.description}
            </Typography>
          </Paper>
        ))}
      </Box>
    </Paper>
  );
};

// Dica informativa
export const InfoTip: React.FC<{
  message: string;
}> = ({ message }) => (
  <Alert
    severity="info"
    icon={<InfoIcon />}
    sx={{
      mt: 3,
      borderRadius: 2,
    }}
  >
    <Typography variant="body2">
      <strong>Dica:</strong> {message}
    </Typography>
  </Alert>
);

// Seletor de tipo de peça (Comum/JEC ou Breve/Longa)
export const PieceTypeSelector: React.FC<{
  value: string;
  onChange: (value: string) => void;
  options: Array<{
    value: string;
    label: string;
    description?: string;
  }>;
}> = ({ value, onChange, options }) => {
  const theme = useTheme();

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        Selecione o tipo da peça que deseja: <Typography component="span" color="error.main">*</Typography>
      </Typography>
      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={(_, newValue) => {
          if (newValue !== null) {
            onChange(newValue);
          }
        }}
        sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}
      >
        {options.map((option) => (
          <ToggleButton
            key={option.value}
            value={option.value}
            sx={{
              px: 4,
              py: 1.5,
              flex: 1,
              minWidth: 150,
              '&.Mui-selected': {
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                borderColor: 'primary.main',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.15),
                }
              }
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle2" fontWeight={600}>{option.label}</Typography>
              {option.description && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {option.description}
                </Typography>
              )}
            </Box>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
};

